import { Box, Button, Input, InputGroup } from "@chakra-ui/react";
import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import api from "../../../lib/api";
import { toaster } from "../../../components/ui/toaster";

export default function ChangePassword() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    // Validation
    if (
      !formData.currentPassword ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      setErrorMessage("Please fill all fields");
      toaster.create({
        title: "Validation Error",
        description: "Please fill all fields",
        type: "error",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage("New passwords do not match");
      toaster.create({
        title: "Validation Error",
        description: "New passwords do not match",
        type: "error",
      });
      return;
    }

    if (formData.newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters long");
      toaster.create({
        title: "Validation Error",
        description: "Password must be at least 8 characters long",
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/auth/change-password", {
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
      });

      if (response.status >= 200) {
        const message = response.data.message || "Password changed successfully";
        setSuccessMessage(message);
        toaster.create({
          title: "Success",
          description: message,
          type: "success",
        });

        // Reset form
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error: any) {
      console.error("Change password error:", error);
      const validationErrors = error.response?.data?.data?.errors;
      const validationErrorMessage = Array.isArray(validationErrors)
        ? validationErrors.map((item: { msg?: string }) => item?.msg).filter(Boolean).join(", ")
        : "";

      const resolvedErrorMessage =
        validationErrorMessage ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to change password";

      setErrorMessage(resolvedErrorMessage);

      toaster.create({
        title: "Error",
        description: resolvedErrorMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box flex={1} p={4} bg="white">
      <Box maxW="2xl">
        <Box fontSize="3xl" fontWeight="bold" color="gray.800" mb={2}>
          Change Password
        </Box>
        <Box color="gray.600" mb={8}>
          Create a new password to secure your account.
        </Box>

        <Box bg="white" borderRadius="xl" borderWidth="1px" p={8}>
          <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={6}>
            <Box>
              <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
                Current Password
              </Box>
              <InputGroup
                endElement={
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      color: "#6B7280",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}>
                    {showCurrentPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                }>
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={formData.currentPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      currentPassword: e.target.value,
                    })
                  }
                  placeholder="Enter current password"
                  borderColor="gray.300"
                  disabled={loading}
                />
              </InputGroup>
            </Box>

            <Box>
              <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
                New Password
              </Box>
              <InputGroup
                endElement={
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      color: "#6B7280",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}>
                    {showNewPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                }>
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, newPassword: e.target.value })
                  }
                  placeholder="Enter new password"
                  borderColor="gray.300"
                  disabled={loading}
                />
              </InputGroup>
            </Box>

            <Box>
              <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
                Confirm Password
              </Box>
              <InputGroup
                endElement={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      color: "#6B7280",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}>
                    {showConfirmPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                }>
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  placeholder="Confirm new password"
                  borderColor="gray.300"
                  disabled={loading}
                />
              </InputGroup>
            </Box>

            {errorMessage && (
              <Box color="red.600" fontSize="sm">
                {errorMessage}
              </Box>
            )}

            {successMessage && (
              <Box color="green.600" fontSize="sm">
                {successMessage}
              </Box>
            )}

            <Box pt={4}>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                px={8}
                loading={loading}
                disabled={loading}>
                Change Password
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
